import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, ChevronDown, HelpCircle, Upload, CreditCard, Shield, MessageSquare } from 'lucide-react';

const CATEGORIES = [
  { id: 'general', label: 'Preguntas Generales', icon: HelpCircle },
  { id: 'archivos', label: 'Cómo Subir Archivos', icon: Upload },
  { id: 'planes', label: 'Planes y Pagos', icon: CreditCard },
  { id: 'seguridad', label: 'Seguridad y Privacidad', icon: Shield },
  { id: 'contacto', label: 'Contacto', icon: MessageSquare },
];

const AccordionItem = ({ question, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen]);

  return (
    <div className="border-b border-gray-800 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-5 px-1 text-left hover:text-cyan-400 transition-colors group"
      >
        <span className={`font-medium text-sm sm:text-base pr-4 ${isOpen ? 'text-cyan-400' : 'text-white'}`}>
          {question}
        </span>
        <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-cyan-400' : 'text-gray-500'}`} />
      </button>
      <div
        style={{ maxHeight: height }}
        className="overflow-hidden transition-all duration-300 ease-in-out"
      >
        <div ref={contentRef} className="pb-5 px-1 text-gray-400 text-sm leading-relaxed space-y-3">
          {children}
        </div>
      </div>
    </div>
  );
};

const GuideStep = ({ number, title, description, image, imageSmall, color = 'cyan' }) => (
  <div className="flex items-start gap-4 my-4">
    <div className={`flex-shrink-0 w-7 h-7 rounded-full bg-${color}-500/20 text-${color}-400 flex items-center justify-center font-bold text-xs`}>
      {number}
    </div>
    <div className="flex-1">
      <h4 className="text-white font-medium text-sm mb-1">{title}</h4>
      <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
      {image && (
        <img
          src={image}
          alt={`Paso ${number}`}
          className={`rounded-lg border border-gray-800 mt-3 ${imageSmall ? 'max-w-sm' : 'w-full'}`}
          loading="lazy"
        />
      )}
    </div>
  </div>
);

const HelpPage = ({ onBack, onNavigate }) => {
  const [activeCategory, setActiveCategory] = useState('general');
  const sectionRefs = useRef({});

  const scrollToSection = (id) => {
    setActiveCategory(id);
    if (id === 'contacto') {
      onNavigate && onNavigate('contact');
      return;
    }
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      <header className="border-b border-gray-800 bg-[#0d0d0f]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            data-testid="help-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>
          <img src="/logo.png" alt="TraceMetrics" className="h-10 w-auto" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <p className="text-cyan-400 text-sm font-medium tracking-wider uppercase mb-2">Centro de ayuda</p>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">¿Cómo podemos ayudarte?</h1>
          <p className="text-gray-500">Elige una categoría para encontrar rápidamente la respuesta que necesitas</p>
        </div>

        {/* Category Buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-14" data-testid="help-categories">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => scrollToSection(cat.id)}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all text-center ${
                  activeCategory === cat.id
                    ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-400'
                    : 'border-gray-800 bg-[#0d0d0f] text-gray-400 hover:border-gray-700 hover:text-gray-300'
                }`}
                data-testid={`help-cat-${cat.id}`}
              >
                <Icon className="w-6 h-6" />
                <span className="text-xs font-medium">{cat.label}</span>
              </button>
            );
          })}
        </div>

        {/* General Questions */}
        <section ref={(el) => (sectionRefs.current['general'] = el)} className="mb-14 scroll-mt-28">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-3">
            <HelpCircle className="w-5 h-5 text-cyan-400" />
            Preguntas Generales
          </h2>
          <div className="bg-[#0d0d0f] border border-gray-800 rounded-2xl px-6">
            <AccordionItem question="¿Qué es TraceMetrics?">
              <p>
                TraceMetrics es una plataforma de análisis de operaciones de trading que te permite importar tu historial
                de operaciones desde diferentes exchanges y obtener un análisis detallado de tu rendimiento.
              </p>
              <p>
                Con TraceMetrics puedes visualizar tu PNL (ganancias y pérdidas) diario, mensual y acumulado,
                comparar tu rendimiento contra Bitcoin, identificar tus mejores y peores días de trading,
                y exportar informes profesionales en PDF.
              </p>
            </AccordionItem>

            <AccordionItem question="¿Qué exchanges soportamos actualmente?">
              <p>Actualmente soportamos los siguientes exchanges y formatos:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li><strong className="text-gray-200">MEXC Futuros</strong> — Archivo Excel (.xlsx/.xls) exportado desde el historial de órdenes de futuros.</li>
                <li><strong className="text-gray-200">MEXC Spot</strong> — Archivo Excel (.xlsx/.xls) exportado desde el historial de operaciones spot.</li>
                <li><strong className="text-gray-200">Quantfury</strong> — Archivo PDF exportado desde el historial de posiciones.</li>
              </ul>
              <p className="mt-2">
                Estamos trabajando constantemente para añadir más exchanges. Si usas una plataforma que no soportamos,
                puedes subir tu archivo igualmente y nuestro equipo lo analizará para integrarlo lo antes posible.
              </p>
            </AccordionItem>

            <AccordionItem question="¿Quién puede usar TraceMetrics?">
              <p>
                TraceMetrics está diseñado para cualquier trader que opere en mercados de criptomonedas, ya sea principiante o avanzado.
                Es especialmente útil para:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Traders de futuros que quieran analizar su rendimiento diario.</li>
                <li>Traders de spot que necesiten calcular su PNL realizado y no realizado.</li>
                <li>Cualquier persona que quiera llevar un control profesional de sus operaciones.</li>
              </ul>
            </AccordionItem>

            <AccordionItem question="¿Dónde está ubicada la empresa?">
              <p>
                TraceMetrics es operado por <strong className="text-gray-200">Noakai LLC</strong>, una empresa constituida en los Estados Unidos de América.
                Operamos a nivel global y nuestros servicios están disponibles para usuarios de cualquier país.
              </p>
            </AccordionItem>

            <AccordionItem question="¿Vendemos los datos de trading de los usuarios?">
              <p>
                <strong className="text-gray-200">No.</strong> Nunca vendemos, alquilamos ni comercializamos los datos de trading de nuestros usuarios con terceros.
                Tus archivos de operaciones son procesados exclusivamente para generar los análisis que solicitas.
                Nuestra fuente de ingresos proviene únicamente de las suscripciones.
              </p>
            </AccordionItem>

            <AccordionItem question="¿Qué pasa con mis datos si cambio o cancelo mi plan?">
              <p>
                Tus datos permanecen intactos en tu cuenta. Cambiar de plan (ya sea actualizar o reducir) no elimina ni altera tu información.
                Tus datos se conservan hasta que decidas eliminar tu cuenta.
              </p>
            </AccordionItem>
          </div>
        </section>

        {/* File Upload Guides */}
        <section ref={(el) => (sectionRefs.current['archivos'] = el)} className="mb-14 scroll-mt-28">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-3">
            <Upload className="w-5 h-5 text-cyan-400" />
            Cómo Subir Archivos
          </h2>
          <div className="bg-[#0d0d0f] border border-gray-800 rounded-2xl px-6">
            <AccordionItem question="¿Cómo exportar mi historial de Futuros desde MEXC?">
              <GuideStep
                number={1}
                title="Accede a la sección de Órdenes"
                description='Desde la interfaz de Futuros de MEXC, haz clic en el botón "Órdenes" situado en la esquina superior derecha de la pantalla.'
                image="/guides/mexc-futures-1.png"
              />
              <GuideStep
                number={2}
                title="Abre el historial de operaciones"
                description='Se abrirá un menú desplegable. Selecciona la opción "Historial de órdenes y operaciones".'
                image="/guides/mexc-futures-2.png"
                imageSmall
              />
              <GuideStep
                number={3}
                title="Exporta el historial"
                description='En la pantalla del historial, localiza y haz clic en "Exportar historial de órdenes" en la parte superior derecha de la tabla.'
                image="/guides/mexc-futures-3.png"
              />
              <GuideStep
                number={4}
                title="Selecciona el periodo y descarga"
                description="Se abrirá una ventana donde podrás elegir el rango de fechas deseado. Selecciona el periodo y confirma la exportación. El archivo se descargará en formato XLS (Excel)."
                image="/guides/mexc-futures-5.png"
                imageSmall
              />
              <GuideStep
                number={5}
                title="Sube el archivo a TraceMetrics"
                description='Guarda el archivo descargado en tu dispositivo e inicia sesión en TraceMetrics. Haz clic en "Seleccionar Archivo" y elige el archivo Excel descargado. El análisis se generará automáticamente.'
                color="emerald"
              />
            </AccordionItem>

            <AccordionItem question="¿Cómo exportar mi historial de Spot desde MEXC?">
              <div className="flex flex-col items-center py-6 text-center">
                <p className="text-gray-400">
                  Estamos preparando la guía paso a paso para exportar tu historial de operaciones Spot desde MEXC.
                  Estará disponible muy pronto.
                </p>
                <p className="text-gray-600 text-xs mt-3">
                  Mientras tanto, puedes exportar tu historial desde MEXC Spot y subir el archivo Excel directamente.
                </p>
              </div>
            </AccordionItem>

            <AccordionItem question="¿Cómo exportar mi historial desde Quantfury?">
              <div className="flex flex-col items-center py-6 text-center">
                <p className="text-gray-400">
                  Estamos preparando la guía paso a paso para exportar tu historial de operaciones desde Quantfury.
                  Estará disponible muy pronto.
                </p>
                <p className="text-gray-600 text-xs mt-3">
                  Mientras tanto, puedes exportar tu historial como PDF desde Quantfury y subirlo directamente.
                </p>
              </div>
            </AccordionItem>

            <AccordionItem question="¿Usas otro exchange?">
              <p>
                Estamos trabajando continuamente para integrar nuevos exchanges. Si tu plataforma de trading
                no está en la lista, puedes subir igualmente tu archivo de historial de operaciones en formato
                <strong className="text-gray-200"> Excel (.xlsx, .xls)</strong> o <strong className="text-gray-200">PDF</strong>.
              </p>
              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4 mt-3">
                <p className="text-cyan-400 text-sm leading-relaxed">
                  Nuestro equipo revisará tu archivo para añadir soporte a tu exchange.
                  Te notificaremos por correo electrónico en cuanto esté disponible.
                </p>
              </div>
            </AccordionItem>

            <AccordionItem question="¿Qué formatos de archivo acepta TraceMetrics?">
              <ul className="list-disc pl-5 space-y-1">
                <li><strong className="text-gray-200">Excel (.xlsx, .xls)</strong> — Para MEXC Futuros y Spot.</li>
                <li><strong className="text-gray-200">PDF</strong> — Para Quantfury.</li>
              </ul>
              <p className="mt-2">
                El tamaño máximo de archivo es de 50 MB. Por seguridad, verificamos que el contenido real del archivo
                coincida con su extensión para prevenir archivos potencialmente dañinos.
              </p>
            </AccordionItem>
          </div>
        </section>

        {/* Plans & Payments */}
        <section ref={(el) => (sectionRefs.current['planes'] = el)} className="mb-14 scroll-mt-28">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-cyan-400" />
            Planes y Pagos
          </h2>
          <div className="bg-[#0d0d0f] border border-gray-800 rounded-2xl px-6">
            <AccordionItem question="¿Qué planes hay disponibles?">
              <p>Ofrecemos tres planes de pago además del plan gratuito:</p>
              <ul className="list-disc pl-5 space-y-2 mt-2">
                <li><strong className="text-gray-200">Plan Gratuito</strong> — Análisis limitado a 20 operaciones por archivo. Ideal para probar la plataforma.</li>
                <li><strong className="text-gray-200">Plan Mensual (2,99 EUR)</strong> — Acceso completo a todas las funcionalidades durante 30 días.</li>
                <li><strong className="text-gray-200">Plan Anual (26,90 EUR)</strong> — Acceso completo durante 365 días. Ahorra un 25% respecto al plan mensual.</li>
                <li><strong className="text-gray-200">Acceso de por Vida (59 EUR)</strong> — Pago único. Acceso ilimitado para siempre.</li>
              </ul>
            </AccordionItem>

            <AccordionItem question="¿Qué métodos de pago aceptamos?">
              <p>
                Procesamos los pagos a través de <strong className="text-gray-200">Stripe</strong>, líder mundial en procesamiento de pagos.
                Aceptamos tarjetas de crédito y débito (Visa, Mastercard, American Express) y otros métodos según tu región.
              </p>
              <p className="mt-2">
                Tu información de pago nunca pasa por nuestros servidores. Stripe gestiona todo el proceso de forma segura
                y está certificado con los más altos estándares de seguridad (PCI DSS Nivel 1).
              </p>
            </AccordionItem>

            <AccordionItem question="¿Puedo cancelar en cualquier momento?">
              <p>
                Sí. No hay compromisos a largo plazo. Los planes son prepagados y puedes cancelar en cualquier momento.
                Seguirás teniendo acceso hasta que expire el periodo pagado.
              </p>
            </AccordionItem>

            <AccordionItem question="¿Cuál es la política de reembolso?">
              <p>
                Dado que ofrecemos un plan gratuito que permite probar la plataforma antes de suscribirse,
                con carácter general no se ofrecen reembolsos una vez procesado el pago. Sin embargo,
                se evaluarán las solicitudes caso por caso en situaciones excepcionales conforme a la legislación aplicable.
              </p>
            </AccordionItem>

            <AccordionItem question="¿Los códigos promocionales son acumulables?">
              <p>
                Los códigos promocionales proporcionan días de acceso premium gratuito. Cada código tiene sus propias
                condiciones de uso (número máximo de usos, validez). Los códigos no son acumulables con otras ofertas
                y están sujetos a disponibilidad.
              </p>
            </AccordionItem>
          </div>
        </section>

        {/* Security & Privacy */}
        <section ref={(el) => (sectionRefs.current['seguridad'] = el)} className="mb-14 scroll-mt-28">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-3">
            <Shield className="w-5 h-5 text-cyan-400" />
            Seguridad y Privacidad
          </h2>
          <div className="bg-[#0d0d0f] border border-gray-800 rounded-2xl px-6">
            <AccordionItem question="¿Es seguro subir mis archivos de trading?">
              <p>
                Sí. Implementamos múltiples capas de seguridad para proteger tus archivos:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li><strong className="text-gray-200">Validación de archivos:</strong> Verificamos que el contenido real del archivo coincida con su extensión (magic bytes) para detectar archivos potencialmente maliciosos.</li>
                <li><strong className="text-gray-200">Procesamiento en memoria:</strong> Tus archivos se procesan en memoria sin almacenarse permanentemente en disco.</li>
                <li><strong className="text-gray-200">Sin ejecución de macros:</strong> Solo leemos datos tabulares. No se ejecutan macros ni código embebido en los archivos.</li>
                <li><strong className="text-gray-200">Comunicación cifrada:</strong> Toda la comunicación entre tu navegador y nuestros servidores está cifrada con HTTPS/TLS.</li>
              </ul>
            </AccordionItem>

            <AccordionItem question="¿Qué datos almacenamos?">
              <p>Almacenamos la información mínima necesaria para prestar el servicio:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li><strong className="text-gray-200">Datos de cuenta:</strong> Nombre y correo electrónico.</li>
                <li><strong className="text-gray-200">Datos de trading:</strong> Los registros extraídos de tus archivos para generar los análisis.</li>
                <li><strong className="text-gray-200">Datos de pago:</strong> Solo referencias de transacciones. Los datos de tarjeta son gestionados directamente por Stripe.</li>
              </ul>
              <p className="mt-2">
                Para más detalles, consulta nuestra <button onClick={() => onNavigate && onNavigate('privacy')} className="text-cyan-400 hover:underline">Política de Privacidad</button>.
              </p>
            </AccordionItem>

            <AccordionItem question="¿Cómo protegemos tu información?">
              <ul className="list-disc pl-5 space-y-1">
                <li>Cifrado de datos en tránsito (HTTPS/TLS).</li>
                <li>Almacenamiento seguro con acceso restringido.</li>
                <li>Autenticación mediante códigos de verificación por email.</li>
                <li>Pagos procesados por Stripe (certificado PCI DSS Nivel 1).</li>
                <li>Infraestructura protegida con firewalls y monitorización.</li>
              </ul>
            </AccordionItem>

            <AccordionItem question="¿Puedo solicitar la eliminación de mis datos?">
              <p>
                Sí. Conforme a la normativa de protección de datos aplicable (RGPD, CCPA), puedes solicitar la eliminación
                de tus datos personales en cualquier momento a través de nuestro
                <button onClick={() => onNavigate && onNavigate('contact')} className="text-cyan-400 hover:underline ml-1">formulario de contacto</button>.
                Procesaremos tu solicitud en el menor tiempo posible.
              </p>
            </AccordionItem>
          </div>
        </section>

        {/* Contact */}
        <section ref={(el) => (sectionRefs.current['contacto'] = el)} className="mb-14 scroll-mt-28">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-cyan-400" />
            ¿No encuentras lo que buscas?
          </h2>
          <div className="bg-[#0d0d0f] border border-gray-800 rounded-2xl p-8 text-center">
            <p className="text-gray-400 mb-6">
              Si no has encontrado respuesta a tu pregunta, nuestro equipo de soporte estará encantado de ayudarte.
            </p>
            <button
              onClick={() => onNavigate && onNavigate('contact')}
              className="px-8 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-lg font-semibold hover:from-cyan-400 hover:to-teal-400 transition-all"
              data-testid="help-contact-btn"
            >
              Contactar con soporte
            </button>
          </div>
        </section>
      </main>

      <footer className="border-t border-gray-800 py-6">
        <div className="max-w-5xl mx-auto px-6 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} TraceMetrics · Noakai LLC. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  );
};

export default HelpPage;
